import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/admin/blog")({
  head: () => ({
    meta: [
      { title: "Admin — Blog" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AdminBlog,
});

interface Row {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  body_markdown: string;
  cover_image_url: string | null;
  author: string;
  tags: string[];
  published: boolean;
  published_at: string | null;
  updated_at: string;
}

const slugify = (s: string) =>
  s.toLowerCase().trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 80);

function AdminBlog() {
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<Row | null>(null);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data }) => {
      if (!data.session) { navigate({ to: "/auth" }); return; }
      const { data: roles } = await supabase
        .from("user_roles").select("role")
        .eq("user_id", data.session.user.id).eq("role", "admin").maybeSingle();
      setIsAdmin(!!roles); setAuthChecked(true);
    });
  }, [navigate]);

  const load = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    const { data } = await (supabase as any)
      .from("blog_posts").select("*").order("updated_at", { ascending: false });
    setRows((data ?? []) as Row[]);
    setLoading(false);
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);

  const newPost = () => {
    setEditing({
      id: "", slug: "", title: "", excerpt: "", body_markdown: "",
      cover_image_url: null, author: "Texas Made Shade & Covers",
      tags: [], published: false, published_at: null, updated_at: "",
    });
  };

  const save = async () => {
    if (!editing) return;
    if (!editing.title.trim()) { setStatus("Title required"); return; }
    const slug = editing.slug.trim() || slugify(editing.title);
    setSaving(true); setStatus("Saving…");
    const payload = {
      slug,
      title: editing.title.trim(),
      excerpt: editing.excerpt,
      body_markdown: editing.body_markdown,
      cover_image_url: editing.cover_image_url,
      author: editing.author,
      tags: editing.tags,
      published: editing.published,
      published_at: editing.published
        ? (editing.published_at ?? new Date().toISOString())
        : null,
    };
    const q = editing.id
      ? (supabase as any).from("blog_posts").update(payload).eq("id", editing.id)
      : (supabase as any).from("blog_posts").insert(payload);
    const { error } = await q;
    setSaving(false);
    if (error) { setStatus(`Error: ${error.message}`); return; }
    setStatus("Saved"); setEditing(null); load();
    setTimeout(() => setStatus(""), 2000);
  };

  const remove = async (id: string) => {
    if (!confirm("Delete this post permanently?")) return;
    const { error } = await (supabase as any).from("blog_posts").delete().eq("id", id);
    if (error) alert(error.message); else load();
  };

  const uploadImage = async (file: File): Promise<string | null> => {
    const ext = file.name.split(".").pop() || "jpg";
    const path = `${crypto.randomUUID()}.${ext}`;
    const { error } = await supabase.storage.from("blog-images").upload(path, file, {
      cacheControl: "31536000", upsert: false,
    });
    if (error) { alert(error.message); return null; }
    const { data } = supabase.storage.from("blog-images").getPublicUrl(path);
    return data.publicUrl;
  };

  const onCoverFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file || !editing) return;
    const url = await uploadImage(file);
    if (url) setEditing({ ...editing, cover_image_url: url });
  };

  const insertImageInBody = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file || !editing) return;
    const url = await uploadImage(file);
    if (url) setEditing({
      ...editing,
      body_markdown: `${editing.body_markdown}\n\n![${file.name}](${url})\n`,
    });
    e.target.value = "";
  };

  if (!authChecked) return <div className="p-10 text-center">Loading…</div>;
  if (!isAdmin) return (
    <div className="p-10 text-center">
      <p>No admin access.</p>
      <Link to="/admin" className="text-rust underline">Back</Link>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b-4 border-navy-deep bg-white px-4 py-4 sm:px-8 flex flex-wrap gap-4 items-center justify-between">
        <div>
          <Link to="/admin" className="text-xs uppercase tracking-wider text-navy-deep/60 hover:text-red">← Admin</Link>
          <h1 className="font-display text-2xl font-bold uppercase text-navy-deep">Blog Posts</h1>
        </div>
        <button onClick={newPost} className="bg-rust px-4 py-2 font-display text-xs uppercase text-white">
          + New Post
        </button>
      </header>

      <div className="px-4 py-6 sm:px-8">
        {loading && <div>Loading…</div>}
        {status && <div className="mb-3 text-sm text-navy-deep/70">{status}</div>}
        <table className="w-full border-2 border-navy-deep bg-white text-sm">
          <thead className="bg-navy-deep text-white">
            <tr>
              <th className="p-2 text-left">Title</th>
              <th className="p-2 text-left">Slug</th>
              <th className="p-2 text-left">Status</th>
              <th className="p-2 text-left">Updated</th>
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-navy-deep/10">
                <td className="p-2 font-bold">{r.title}</td>
                <td className="p-2 text-navy-deep/70">/blog/{r.slug}</td>
                <td className="p-2">
                  {r.published ? (
                    <span className="bg-green-100 px-2 py-1 text-xs uppercase">Published</span>
                  ) : (
                    <span className="bg-gray-200 px-2 py-1 text-xs uppercase">Draft</span>
                  )}
                </td>
                <td className="p-2 text-navy-deep/60">{new Date(r.updated_at).toLocaleDateString()}</td>
                <td className="p-2 text-right whitespace-nowrap">
                  <button onClick={() => setEditing(r)} className="mr-2 underline">Edit</button>
                  <button onClick={() => remove(r.id)} className="text-red-600 underline">Delete</button>
                </td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center text-navy-deep/60">
                No posts yet. Click "+ New Post" to write your first one.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4">
          <div className="my-8 w-full max-w-4xl bg-white p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl font-bold uppercase">
                {editing.id ? "Edit Post" : "New Post"}
              </h2>
              <button onClick={() => setEditing(null)} className="text-navy-deep/60">✕</button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-wider mb-1">Title *</label>
                <input
                  className="w-full border-2 border-navy-deep px-3 py-2"
                  value={editing.title}
                  onChange={(e) => setEditing({
                    ...editing,
                    title: e.target.value,
                    slug: editing.slug || slugify(e.target.value),
                  })}
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider mb-1">URL slug</label>
                <input
                  className="w-full border-2 border-navy-deep px-3 py-2"
                  value={editing.slug}
                  onChange={(e) => setEditing({ ...editing, slug: slugify(e.target.value) })}
                />
                <p className="mt-1 text-xs text-navy-deep/60">/blog/{editing.slug || "your-slug"}</p>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider mb-1">Excerpt (1–2 sentences, used for SEO description)</label>
                <textarea
                  className="w-full border-2 border-navy-deep px-3 py-2"
                  rows={2}
                  value={editing.excerpt}
                  onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider mb-1">Cover image</label>
                {editing.cover_image_url && (
                  <img src={editing.cover_image_url} alt="Blog post cover preview" className="mb-2 max-h-48" />
                )}
                <input type="file" accept="image/*" onChange={onCoverFile} />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider mb-1">
                  {"Body (Markdown — **bold**, *italic*, # headings, [links](url), ![alt](image), lists, > quotes)"}
                </label>
                <textarea
                  className="w-full border-2 border-navy-deep px-3 py-2 font-mono text-sm"
                  rows={18}
                  value={editing.body_markdown}
                  onChange={(e) => setEditing({ ...editing, body_markdown: e.target.value })}
                />
                <div className="mt-2 flex items-center gap-2 text-xs">
                  <label className="cursor-pointer underline">
                    Insert image into body
                    <input type="file" accept="image/*" className="hidden" onChange={insertImageInBody} />
                  </label>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-xs uppercase tracking-wider mb-1">Author</label>
                  <input
                    className="w-full border-2 border-navy-deep px-3 py-2"
                    value={editing.author}
                    onChange={(e) => setEditing({ ...editing, author: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider mb-1">Tags (comma-separated)</label>
                  <input
                    className="w-full border-2 border-navy-deep px-3 py-2"
                    value={editing.tags.join(", ")}
                    onChange={(e) => setEditing({
                      ...editing,
                      tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean),
                    })}
                  />
                </div>
              </div>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editing.published}
                  onChange={(e) => setEditing({ ...editing, published: e.target.checked })}
                />
                <span className="text-sm">Published (visible to everyone)</span>
              </label>

              <div className="flex gap-2 justify-end pt-4 border-t">
                <button
                  onClick={() => setEditing(null)}
                  className="border-2 border-navy-deep px-4 py-2 text-xs uppercase"
                >Cancel</button>
                <button
                  onClick={save}
                  disabled={saving}
                  className="bg-rust px-4 py-2 text-xs uppercase text-white disabled:opacity-50"
                >{saving ? "Saving…" : "Save"}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
